import axios from 'axios';
import { API_BASE_URL } from '../../config/env';
import {
  AuthRole,
  AuthTokenBundle,
  getAuthTokenBundle,
  getWebSessionContext,
  isUsableStoredToken,
  saveAuthTokenBundle,
  tokenExpiresSoon,
} from '../storage/authTokens';

const refreshApi = axios.create({
  baseURL: API_BASE_URL,
});

const inFlightRefreshes: Partial<Record<AuthRole, Promise<string | null>>> = {};

function refreshEndpointForRole(role: AuthRole): string {
  return role === 'driver'
    ? '/driver-auth-service/v2/auth/refresh'
    : '/auth-service/v1/auth/refresh';
}

function logoutEndpointForRole(role: AuthRole): string {
  return role === 'driver'
    ? '/driver-auth-service/v2/auth/logout'
    : '/auth-service/v1/auth/logout';
}

export function normalizeTokenBundle(response: any): AuthTokenBundle {
  return {
    accessToken: response?.accessToken ?? response?.access_token ?? response?.token ?? null,
    refreshToken: response?.refreshToken ?? response?.refresh_token ?? null,
    accessTokenExpiresAt: response?.accessTokenExpiresAt ?? response?.access_token_expires_at ?? null,
    refreshTokenExpiresAt: response?.refreshTokenExpiresAt ?? response?.refresh_token_expires_at ?? null,
  };
}

export function hasUsableAccessToken(role: AuthRole): boolean {
  const bundle = getAuthTokenBundle(role);
  return Boolean(bundle.accessToken && !tokenExpiresSoon(bundle.accessTokenExpiresAt));
}

export async function refreshRoleSession(role: AuthRole): Promise<string | null> {
  if (inFlightRefreshes[role]) {
    return inFlightRefreshes[role] as Promise<string | null>;
  }

  const refreshPromise = (async () => {
    const current = getAuthTokenBundle(role);
    if (!isUsableStoredToken(current.refreshToken)) {
      return null;
    }

    const refreshToken = current.refreshToken.trim();
    const response = await refreshApi.post(refreshEndpointForRole(role), {
      refreshToken,
      refresh_token: refreshToken,
      ...getWebSessionContext(),
    });

    const nextBundle = normalizeTokenBundle(response.data);
    if (!nextBundle.accessToken || !nextBundle.refreshToken) {
      return null;
    }

    saveAuthTokenBundle(role, nextBundle);
    return nextBundle.accessToken;
  })();

  inFlightRefreshes[role] = refreshPromise;

  try {
    return await refreshPromise;
  } finally {
    delete inFlightRefreshes[role];
  }
}

export async function refreshRoleSessionIfNeeded(role: AuthRole): Promise<string | null> {
  const bundle = getAuthTokenBundle(role);
  if (!bundle.accessToken) {
    return isUsableStoredToken(bundle.refreshToken) ? refreshRoleSession(role) : null;
  }

  if (tokenExpiresSoon(bundle.accessTokenExpiresAt)) {
    return isUsableStoredToken(bundle.refreshToken) ? refreshRoleSession(role) : bundle.accessToken;
  }

  return bundle.accessToken;
}

export async function logoutRoleSession(role: AuthRole): Promise<void> {
  const refreshToken = getAuthTokenBundle(role).refreshToken;
  if (!isUsableStoredToken(refreshToken)) return;

  await refreshApi.post(logoutEndpointForRole(role), {
    refreshToken: refreshToken.trim(),
    refresh_token: refreshToken.trim(),
  });
}
